import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '@altermaind/core/i18n';
import { Box, Svg } from '@altermaind/web/ui';
import {
  Column,
  PaginatedTable,
  StyledTable,
  StyledTableDrawer,
  TableHeader,
  ValueInTable,
  useDrawer,
  usePaginatedTable,
} from '@/_shared/components';
import { Routes } from '@/_shared/constants/routes';
import { UNKNOWN_DEPARTMENT_ID } from '@/departments/state/constants';
import { getEmployeesCall } from '@/people/state';
import { PAGE_SIZE_EMPLOYEES } from '@/people/state/constants';
import { Employer } from '@/people/state/types';

export const UnassignedMembers = () => {
  const t = useTranslations('departments.list.unassignedMembers');
  const navigate = useNavigate();
  const [selectedMember, setSelectedMember] = useState<Employer | undefined>();
  const { isOpen, close, ...drawerProps } = useDrawer();
  const drawers: StyledTableDrawer['instances'] = [];

  const {
    execute: executeGetEmployees,
    data,
    totalItems,
    loading: loadingGetEmployees,
    error: errorGetEmployees,
  } = getEmployeesCall.hook();

  const paginedProps = usePaginatedTable({
    rowData: data || [],
    totalItems: totalItems || 0,
    pageSize: PAGE_SIZE_EMPLOYEES,
  });

  const fetchEmployees = (q?: string) => {
    executeGetEmployees({
      departments: [UNKNOWN_DEPARTMENT_ID],
      q,
      start: 0,
      length: PAGE_SIZE_EMPLOYEES,
    });
    paginedProps.goToFirst();
  };

  const goToDetail = (id: string) => {
    navigate(Routes.PeopleDetail.replace(':peopleId', id));
  };

  const columns: Column[] = [
    {
      key: 'firstName',
      title: t('table.firstName'),
      renderValue: (item: Employer) => <ValueInTable>{item.firstName || '-'}</ValueInTable>,
    },
    {
      key: 'lastName',
      title: t('table.lastName'),
      renderValue: (item: Employer) => <ValueInTable>{item.lastName || '-'}</ValueInTable>,
    },
    {
      key: 'email',
      title: t('table.email'),
      renderValue: (item: Employer) => <ValueInTable>{item.email || '-'}</ValueInTable>,
    },
    {
      key: 'role',
      title: t('table.role'),
      renderValue: (item: Employer) => <ValueInTable>{item.role || '-'}</ValueInTable>,
    },
    {
      key: 'edit',
      title: '',
      width: '60px',
      renderValue: (item: Employer) =>
        item.id === selectedMember?.id ? (
          <Box
            display='flex'
            flexDirection='row'
            alignItems='flex-end'
            justifyContent='flex-end'
            width='100%'
            style={{ cursor: 'pointer' }}
            onClick={() => item.id && goToDetail(item.id)}
          >
            <Svg name='arrowRight' size='s' />
          </Box>
        ) : null,
    },
  ];

  useEffect(() => {
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box mt='l'>
      <TableHeader
        title={t('title')}
        description={t('description')}
        searchLabel={t('table.searchPlaceholder')}
        onSearchCallback={value => fetchEmployees(value || undefined)}
      />
      <StyledTable
        error={!!errorGetEmployees}
        loading={loadingGetEmployees}
        hasElement={false}
        drawer={{ ...drawerProps, isOpen, close, instances: drawers }}
      >
        <PaginatedTable
          columns={columns}
          // eslint-disable-next-line react/jsx-props-no-spreading
          call={{ ...getEmployeesCall }}
          paginedProps={paginedProps}
          pageSize={PAGE_SIZE_EMPLOYEES}
          onRowClick={(employee: Employer) => employee.id && goToDetail(employee.id)}
          onHoverRow={(employee: Employer) => setSelectedMember(employee)}
          rowWrapperProps={(employee: Employer) =>
            employee.id === selectedMember?.id
              ? {
                  backgroundColor: 'grey/dark/background',
                  borderRadius: '10px',
                  cursor: 'pointer !important',
                }
              : {}
          }
          hasHover
        />
      </StyledTable>
    </Box>
  );
};
